import { createContext, type PropsWithChildren, Suspense, use, useContext, useMemo } from 'react';
import { getPrivacyTranslation } from '@/lib/privacy-info';
import type { PrivacyInfo, PrivacyTranslationKey } from '@/types/privacy-info';

type PrivacyInfoContextValue = {
    privacyInfo: PrivacyInfo | null;
};

const PrivacyInfoContext = createContext<PrivacyInfoContextValue | undefined>(undefined);

type PrivacyInfoProviderProps = PropsWithChildren<{
    /**
     * Either a resolved value or an unresolved Promise. Passing a Promise keeps the
     * route loader non-blocking — the privacy data resolves inside a Suspense
     * boundary instead of stalling the loader (which would risk Page Designer timeouts).
     */
    privacyInfo: PrivacyInfo | null | Promise<PrivacyInfo | null>;
}>;

function isPromise(value: PrivacyInfoProviderProps['privacyInfo']): value is Promise<PrivacyInfo | null> {
    return typeof (value as Promise<PrivacyInfo | null>)?.then === 'function';
}

function ResolvedPrivacyInfoProvider({
    children,
    privacyInfo,
}: PropsWithChildren<{ privacyInfo: PrivacyInfo | null }>) {
    const value = useMemo(() => ({ privacyInfo }), [privacyInfo]);
    return <PrivacyInfoContext.Provider value={value}>{children}</PrivacyInfoContext.Provider>;
}

function DeferredPrivacyInfoProvider({
    children,
    privacyInfo,
}: PropsWithChildren<{ privacyInfo: Promise<PrivacyInfo | null> }>) {
    const resolved = use(privacyInfo);
    return <ResolvedPrivacyInfoProvider privacyInfo={resolved}>{children}</ResolvedPrivacyInfoProvider>;
}

export default function PrivacyInfoProvider({ children, privacyInfo }: PrivacyInfoProviderProps) {
    if (isPromise(privacyInfo)) {
        // While the privacy data is in flight, render children with a null context so the
        // page paints immediately; the resolved value swaps in once the Promise settles.
        return (
            <Suspense
                fallback={<ResolvedPrivacyInfoProvider privacyInfo={null}>{children}</ResolvedPrivacyInfoProvider>}>
                <DeferredPrivacyInfoProvider privacyInfo={privacyInfo}>{children}</DeferredPrivacyInfoProvider>
            </Suspense>
        );
    }
    return <ResolvedPrivacyInfoProvider privacyInfo={privacyInfo}>{children}</ResolvedPrivacyInfoProvider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePrivacyInfo(): PrivacyInfoContextValue {
    const context = useContext(PrivacyInfoContext);
    if (!context) {
        throw new Error('usePrivacyInfo must be used within a PrivacyInfoProvider');
    }
    return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePrivacyTranslation(key: PrivacyTranslationKey): string | null {
    const { privacyInfo } = usePrivacyInfo();
    return useMemo(() => getPrivacyTranslation(privacyInfo, key), [privacyInfo, key]);
}
